
import React from 'react';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface LeagueTableControlsProps {
  viewType: 'all' | 'home' | 'away';
  timeType: 'fullTime' | 'halfTime';
  onViewTypeChange: (type: 'all' | 'home' | 'away') => void;
  onTimeTypeChange: (type: 'fullTime' | 'halfTime') => void;
  selectedDate?: Date;
  onDateChange?: (date: Date | undefined) => void;
  year?: string;
}

const LeagueTableControls: React.FC<LeagueTableControlsProps> = ({
  viewType,
  timeType,
  onViewTypeChange,
  onTimeTypeChange,
  selectedDate,
  onDateChange,
  year = '2024'
}) => {
  // Set the date range based on the year
  // For example, if year is 2024, season is 2024/2025
  const seasonYear = parseInt(year);
  const fromDate = new Date(seasonYear, 7, 1); // August 1st of the season year
  const toDate = new Date(seasonYear + 1, 4, 31); // May 31st of the following year

  const handleDateSelect = (date: Date | undefined) => {
    console.log('Selected date:', date);
    if (onDateChange) {
      onDateChange(date);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-4 w-full px-4">
      <div className="flex items-center gap-2">
        <div className="flex gap-2">
          <Button
            variant={timeType === 'fullTime' ? 'default' : 'outline'}
            onClick={() => onTimeTypeChange('fullTime')}
            className={timeType === 'fullTime' ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
          >
            Full Time
          </Button>
          <Button
            variant={timeType === 'halfTime' ? 'default' : 'outline'}
            onClick={() => onTimeTypeChange('halfTime')}
            className={timeType === 'halfTime' ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
          >
            Half Time
          </Button>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <CalendarIcon className="h-4 w-4" />
              {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
              fromDate={fromDate}
              toDate={toDate}
              disabled={(date) => 
                date < fromDate || date > toDate
              }
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-wrap justify-center sm:justify-end gap-2">
        <Button
          variant={viewType === 'all' ? 'default' : 'outline'}
          onClick={() => onViewTypeChange('all')}
          className={viewType === 'all' ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
        >
          All Matches
        </Button>
        <Button
          variant={viewType === 'home' ? 'default' : 'outline'}
          onClick={() => onViewTypeChange('home')}
          className={viewType === 'home' ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
        >
          Home Only
        </Button>
        <Button
          variant={viewType === 'away' ? 'default' : 'outline'}
          onClick={() => onViewTypeChange('away')}
          className={viewType === 'away' ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
        >
          Away Only
        </Button>
      </div>
    </div>
  );
};

export default LeagueTableControls;
